export const WHATSAPP_BASE_PATH = '/operations/whatsapp';
export const WHATSAPP_SEND_PATH = `${WHATSAPP_BASE_PATH}/send`;
export const WHATSAPP_TESTING_PATH = `${WHATSAPP_BASE_PATH}/testing`;

export const WHATSAPP_TABS = [
  { id: 'inbox', label: 'Inbox', path: WHATSAPP_BASE_PATH },
  { id: 'send', label: 'Send', path: WHATSAPP_SEND_PATH },
  { id: 'contacts', label: 'Contacts', path: `${WHATSAPP_BASE_PATH}/contacts` },
  { id: 'campaigns', label: 'Campaigns', path: `${WHATSAPP_BASE_PATH}/campaigns` },
  { id: 'templates', label: 'Templates', path: `${WHATSAPP_BASE_PATH}/templates` },
  { id: 'notifications', label: 'Notifications', path: `${WHATSAPP_BASE_PATH}/notifications` },
  { id: 'retargeting', label: 'Retargeting', path: `${WHATSAPP_BASE_PATH}/retargeting` },
  { id: 'delivery', label: 'Delivery', path: `${WHATSAPP_BASE_PATH}/delivery` },
  { id: 'analytics', label: 'Analytics', path: `${WHATSAPP_BASE_PATH}/analytics` },
  { id: 'settings', label: 'Settings', path: `${WHATSAPP_BASE_PATH}/settings` },
  { id: 'testing', label: 'Testing', path: WHATSAPP_TESTING_PATH },
] as const;

export type WhatsAppTabId = (typeof WHATSAPP_TABS)[number]['id'];

export interface WhatsAppConversation {
  countryCode: string;
  phoneNumber: string;
  phone: string;
  maskedPhone: string;
  contactName: string;
  lastMessagePreview: string;
  lastMessageAt: string;
  unreadCount: number;
  lastOutboundStatus?: string | null;
  recipientType: string;
  linkedUserId?: string | null;
  linkedVendorId?: string | null;
  tags?: string[];
  deliveryOnly?: boolean;
}

export interface WhatsAppThreadMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  renderedBody?: string | null;
  templateName?: string | null;
  templateBodyValues?: string[] | null;
  templateBodyPreview?: string | null;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  messageType: string;
  status: string;
  isRead: boolean;
  interaktMessageId?: string | null;
  deliveryLogId?: string | null;
  createdAt: string;
  metadata?: Record<string, unknown> | null;
}

export interface WhatsAppContactInsights {
  stats: {
    totalMessages: number;
    outbound: number;
    inbound: number;
    unreadInbound: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    firstContactAt?: string | null;
    lastMessageAt?: string | null;
    lastInboundAt?: string | null;
    lastOutboundAt?: string | null;
  };
  templateUsage: Array<{ name: string; count: number }>;
  recentInbound: WhatsAppThreadMessage[];
  deliveryLogs: Array<{
    id: string;
    templateName?: string | null;
    status: string;
    source?: string | null;
    eventType?: string | null;
    sentAtUtc?: string | null;
    deliveredAt?: string | null;
    readAt?: string | null;
    errorMessage?: string | null;
    interaktMessageId?: string | null;
    createdAt: string;
  }>;
  timeline: Array<{
    id: string;
    kind: string;
    at: string;
    direction?: string;
    body?: string;
    templateName?: string | null;
    status?: string;
    source?: string | null;
    eventType?: string | null;
    errorMessage?: string | null;
    headerMediaUrl?: string | null;
  }>;
}

export interface WhatsAppThreadContact {
  countryCode: string;
  phoneNumber: string;
  phone?: string;
  maskedPhone: string;
  displayName: string;
  email?: string | null;
  city?: string | null;
  recipientType: string;
  linkedUserId?: string | null;
  linkedVendorId?: string | null;
  tags: string[];
  notes: string;
  optedIn: boolean;
}

export interface WhatsAppAnalytics {
  periodDays: number;
  filters?: {
    source?: string | null;
    eventType?: string | null;
    templateName?: string | null;
    messageCategory?: string | null;
    recipientType?: string | null;
  };
  totals: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    inbound: number;
    outbound: number;
    queued?: number;
  };
  billableMessages?: number;
  estimatedCostInr?: number;
  costPerMessageInr?: Record<string, number>;
  uniqueContacts: number;
  deliveryRate: number;
  readRate: number;
  eventBreakdown?: WhatsAppAnalyticsBreakdownRow[];
  templateBreakdown?: WhatsAppAnalyticsBreakdownRow[];
  categoryBreakdown?: WhatsAppAnalyticsBreakdownRow[];
  dailyTrend?: Array<{ day: string; messages: number; estimatedCostInr: number }>;
  recentCampaigns: Array<{
    id: string;
    name: string;
    status: string;
    messageCategory?: string;
    recipientCount: number;
    totalSent: number;
    totalDelivered: number;
    totalRead: number;
    totalFailed: number;
    estimatedCostInr?: number;
    sentAt?: string | null;
  }>;
}

export interface WhatsAppAnalyticsBreakdownRow {
  eventType?: string | null;
  templateName?: string | null;
  messageCategory?: string | null;
  recipientType?: string | null;
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  estimatedCostInr: number;
}

export interface WhatsAppTemplateVariableMapping {
  slot: number;
  label: string;
  contextKey: string | null;
  sourceType: 'context' | 'static' | 'deep_link';
  staticValue?: string;
  deepLinkCampaignId?: string | null;
  deepLinkUrlType?: 'short' | 'full' | 'dynamic';
  deepLinkParamBindings?: Record<string, string>;
}

export interface WhatsAppCtaDeepLinkConfig {
  buttonIndex: number;
  deepLinkCampaignId: string;
  urlType: 'short' | 'full' | 'dynamic';
  paramBindings?: Record<string, string>;
}

export interface WhatsAppContextField {
  key: string;
  label: string;
  sourceType: string;
}

export interface WhatsAppTemplateRuleCondition {
  field: string;
  operator: 'eq' | 'neq' | 'in' | 'contains' | 'starts_with';
  value: string | string[];
}

export interface WhatsAppTemplateRule {
  id: string;
  label: string;
  enabled?: boolean;
  templateName: string;
  languageCode?: string | null;
  conditions: WhatsAppTemplateRuleCondition[];
  templateVariableMappings?: WhatsAppTemplateVariableMapping[];
}

export interface WhatsAppNotificationEvent {
  id?: string;
  eventKey: string;
  eventType: string;
  fcmType?: string | null;
  enabled: boolean;
  whatsappEnabled: boolean;
  templateWorking?: boolean | number | string | null;
  linkNavigationWorking?: boolean | number | string | null;
  templateHealthCheckedAt?: string | null;
  linkHealthCheckedAt?: string | null;
  whatsappSupported: boolean;
  whatsappWired?: boolean;
  category: string;
  messageCategory?: string;
  templateName: string | null;
  defaultTemplateName?: string | null;
  templateRules?: WhatsAppTemplateRule[];
  languageCode: string;
  recipientType: string;
  service: string;
  label: string;
  pushTitle?: string;
  sampleMessage: string;
  trigger: string;
  triggerDescription?: string;
  bodyValueKeys: string[];
  templateVariableMappings?: WhatsAppTemplateVariableMapping[];
  whatsappCtaConfig?: WhatsAppCtaDeepLinkConfig | null;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerFileName?: string | null;
  availableContextFields?: WhatsAppContextField[];
  stats: {
    total: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    queued: number;
    estimatedCostInr: number;
    billableCount: number;
  };
  recentDeliveries?: Array<{
    id: string;
    templateName?: string | null;
    messageCategory?: string | null;
    recipientType?: string | null;
    status: string;
    estimatedCostInr: number;
    createdAt: string;
  }>;
}

export interface WhatsAppNotificationCatalog {
  periodDays: number;
  period?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom' | null;
  periodLabel?: string;
  startDate?: string;
  endDate?: string;
  tabs: Array<{
    id: string;
    label: string;
    count: number;
    items?: WhatsAppNotificationEvent[];
  }>;
  items: WhatsAppNotificationEvent[];
  filters: {
    services: string[];
    categories: string[];
  };
  totals: {
    events: number;
    enabledEvents: number;
    totalMessages: number;
    estimatedCostInr: number;
  };
  costPerMessageInr: Record<string, number>;
}

export interface WhatsAppDeliveryLog {
  id: string;
  source: string;
  eventType?: string | null;
  recipientType?: string | null;
  recipientName?: string | null;
  phoneNumber?: string | null;
  countryCode?: string | null;
  maskedPhone: string;
  templateName?: string | null;
  messageCategory?: string | null;
  status: string;
  interaktMessageId?: string | null;
  campaignId?: string | null;
  channelFailureReason?: string | null;
  errorMessage?: string | null;
  sentAtUtc?: string | null;
  deliveredAt?: string | null;
  readAt?: string | null;
  lastWebhookType?: string | null;
  doctorName?: string | null;
  patientName?: string | null;
  vendorId?: string | null;
  metadata?: {
    doctorName?: string | null;
    vendorName?: string | null;
    patientName?: string | null;
    clinicName?: string | null;
    contactName?: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export type WhatsAppDeliveryStatusCounts = {
  queued: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  total: number;
};

export interface WhatsAppWebhookEvent {
  id: string;
  at: string;
  type: string;
  phone: string | null;
  status: string;
  detail: string | null;
  interaktMessageId?: string | null;
  signatureValid?: boolean;
  processed?: boolean;
  isIncomingMessage?: boolean;
  isDeliveryWebhook?: boolean;
}

export interface WhatsAppWebhookEventStats {
  totalBuffered: number;
  last24h: number;
  incomingLast24h: number;
  deliveryLast24h: number;
  signatureFailuresLast24h: number;
  lastEventAt: string | null;
  lastIncomingAt: string | null;
}

export interface WhatsAppWebhookEventsResponse {
  stats: WhatsAppWebhookEventStats;
  events: WhatsAppWebhookEvent[];
}

export interface WhatsAppWebhookConfig {
  webhookUrl: string | null;
  devMirrorUrl?: string | null;
  prodBaseUrl?: string | null;
  devBaseUrl?: string | null;
  webhookSecretConfigured: boolean;
  interaktConfigured: boolean;
  globallyEnabled?: boolean;
  rcsEnabled?: boolean;
  supportedEvents: string[];
  trackedSources: string[];
  setupNotes: string;
}

export interface WhatsAppReferenceVariable {
  key: string;
  label: string;
  sourceType: 'context' | 'deep_link' | 'static';
  autoFromDb: boolean;
  description?: string | null;
}

export interface WhatsAppNumberedSlot extends WhatsAppReferenceVariable {
  slot: number;
  templateVar: string;
}

export interface WhatsAppReferenceWiredEvent {
  eventKey: string;
  label: string;
  recipientType: string;
  triggerDescription: string;
  configureIn: 'notifications' | 'settings';
}

export interface WhatsAppReferenceModule {
  id: string;
  title: string;
  adminPath: string;
  category: string;
  description: string;
}

export interface WhatsAppVariableCatalogGroup {
  id: string;
  label: string;
  fields: Array<WhatsAppContextField & { refNo?: number | null }>;
}

export interface WhatsAppVariableCatalog {
  groups: WhatsAppVariableCatalogGroup[];
  deepLinkSlots: Array<{ key: string; label: string; sourceType: string; refNo?: number }>;
  allContextFields: Array<WhatsAppContextField & { refNo?: number | null }>;
}

export interface WhatsAppReferenceGuide {
  sendModules: WhatsAppReferenceModule[];
  numberedSlots: WhatsAppNumberedSlot[];
  standardVariables: WhatsAppReferenceVariable[];
  wiredNotificationEvents: WhatsAppReferenceWiredEvent[];
  mappingNotes: string[];
  variableCatalog?: WhatsAppVariableCatalog;
}

export type WhatsAppTestSendMode = 'template' | 'notification' | 'campaign';

export interface WhatsAppTestSendContext {
  mode: WhatsAppTestSendMode;
  templateName: string;
  languageCode: string;
  messageCategory: string;
  recipientType: string;
  variableCount: number;
  bodyPreview?: string | null;
  headerFormat?: string | null;
  headerText?: string | null;
  footer?: string | null;
  buttons?: import('./whatsappCampaign').WhatsAppTemplateButton[];
  sendRequirements?: {
    headerFormat?: string | null;
    headerText?: string | null;
    footer?: string | null;
    requiresHeaderMedia: boolean;
    buttons: Array<{
      index: number;
      type: string;
      text: string;
      urlTemplate?: string | null;
      requiresUrl: boolean;
      phoneNumber?: string | null;
    }>;
  };
  defaultVariableMappings?: WhatsAppTemplateVariableMapping[];
  requiredContextKeys: string[];
  availableContextFields?: WhatsAppContextField[];
  sampleContext: Record<string, string>;
  notification?: {
    eventKey: string;
    label: string;
    whatsappEnabled: boolean;
    whatsappWired?: boolean;
    messageCategory: string;
    sampleMessage?: string;
  } | null;
  campaign?: {
    id: string;
    name: string;
    status: string;
    templateName: string;
    bodyValues?: string[];
  } | null;
}

export interface WhatsAppTestSendResult {
  sent: boolean;
  reason?: string | null;
  deliveryLogId?: string | null;
  interaktMessageId?: string | null;
  phone?: string;
  countryCode?: string;
  phoneNumber?: string;
  mode?: WhatsAppTestSendMode;
  templateName?: string;
  languageCode?: string;
  messageCategory?: string;
  eventType?: string;
  resolvedBodyValues?: string[];
  renderedBody?: string;
  inboxMessageId?: string | null;
}

export interface WhatsAppHubSettings extends WhatsAppWebhookConfig {
  enabled: boolean;
  defaultCountryCode?: string;
  defaultLanguageCode?: string;
  ivrTemplateName?: string | null;
  /** @deprecated Legacy backend allowlist — use referenceGuide + Notifications tab */
  transactionalEvents?: string[];
  /** @deprecated Legacy event config — configure in Notifications tab */
  eventConfig?: Record<string, { templateName?: string | null; enabled?: boolean; category?: string }>;
  referenceGuide?: WhatsAppReferenceGuide;
  rcs?: RcsHubStatus | null;
}

export interface RcsTemplate {
  name: string;
  displayName?: string;
  languageCode?: string;
  category?: string;
  bodyPreview?: string | null;
  variableCount?: number;
  headerFormat?: string | null;
  headerText?: string | null;
  footer?: string | null;
  buttons?: Array<{ type?: string; text?: string; url?: string | null }>;
  approvalStatus?: string | null;
  isApproved?: boolean;
  assistantId?: string | null;
  channel?: 'rcs';
  source?: string;
}

export interface RcsHubStatus {
  enabled: boolean;
  ready: boolean;
  templateName: string | null;
  apiPath: string;
  assistantId: string | null;
  sendPaths?: string[];
  blockers: string[];
  note: string;
  templates?: RcsTemplate[];
  templatesSource?: 'interakt' | 'env' | 'none' | string;
  templateListError?: string | null;
  interaktSetup?: string[];
}

export interface RcsDeliveryLog {
  id: string;
  campaignId?: string | null;
  phone: string;
  maskedPhone?: string;
  rcsTemplateName?: string;
  status: string;
  interaktMessageId?: string | null;
  errorMessage?: string | null;
  sentAt?: string | null;
  deliveredAt?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string | null;
}

export interface WhatsAppContact {
  id: string;
  countryCode: string;
  phoneNumber: string;
  phone: string;
  maskedPhone: string;
  displayName: string;
  tags: string[];
  notes: string;
  linkedUserId?: string | null;
  linkedVendorId?: string | null;
  lastMessageAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  email?: string | null;
  city?: string | null;
  recipientType?: string;
}

export type WhatsAppSendRecipient = {
  key: string;
  countryCode: string;
  phoneNumber: string;
  displayName: string;
  maskedPhone: string;
  source?: string;
  recipientType?: string;
  recipientId?: string | null;
  platform?: string | null;
  vendorKind?: string | null;
  email?: string | null;
  city?: string | null;
  tags?: string[];
};

export type WhatsAppSendTemplateLocationState = {
  recipients?: WhatsAppSendRecipient[];
};
